import { useCallback, useEffect, useRef, useState } from "react";
import { addReminder, loadReminders } from "../utils/reminderStorage";
import {
  extractReminderContent,
  formatReminderTime,
  parseReminderTime,
} from "../utils/parseReminderTime";
import {
  checkDueReminders,
  requestNotificationPermission,
  resyncReminderTimers,
} from "../utils/reminderNotifier";

function getSpeechRecognition() {
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export default function VoiceReminderFab() {
  const [listening, setListening] = useState(false);
  const [liveText, setLiveText] = useState("");
  const [toast, setToast] = useState("");
  const recognitionRef = useRef(null);
  const textRef = useRef("");
  const shouldSaveRef = useRef(false);
  const toastTimerRef = useRef(null);

  const refreshList = useCallback(() => {
    const list = loadReminders().filter((r) => !r.notified);
    resyncReminderTimers(list);
  }, []);

  const showToast = useCallback((msg, ms = 3500) => {
    clearTimeout(toastTimerRef.current);
    setToast(msg);
    if (ms > 0) {
      toastTimerRef.current = window.setTimeout(() => setToast(""), ms);
    }
  }, []);

  const saveReminder = useCallback(
    async (text) => {
      if (!text.trim()) {
        showToast("没听清，请按住备忘键再说一次");
        return;
      }

      const remindAt = parseReminderTime(text);
      if (!remindAt) {
        showToast("没听清时间，例如：一分钟后提醒我喝水");
        return;
      }

      if (remindAt.getTime() <= Date.now()) {
        showToast("提醒时间须晚于现在，请重新说");
        return;
      }

      await requestNotificationPermission();

      const previewContent = extractReminderContent(text) || text.trim();
      addReminder({
        content: previewContent,
        remindAt: remindAt.toISOString(),
        sourceText: text.trim(),
      });

      showToast(`已设置：${formatReminderTime(remindAt)} 提醒您`);
      refreshList();
      checkDueReminders();
      window.dispatchEvent(new CustomEvent("voiceReminderUpdated"));
    },
    [refreshList, showToast]
  );

  const stopListen = useCallback((save = false) => {
    shouldSaveRef.current = save;
    recognitionRef.current?.stop?.();
  }, []);

  const startListen = useCallback(() => {
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition) {
      showToast("当前浏览器不支持语音识别，请用 Chrome 或 Edge");
      return;
    }

    textRef.current = "";
    setLiveText("");
    shouldSaveRef.current = false;

    const recognition = new SpeechRecognition();
    recognition.lang = "zh-CN";
    recognition.interimResults = true;
    recognition.continuous = true;

    recognition.onstart = () => {
      setListening(true);
    };

    recognition.onresult = (event) => {
      let chunk = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        chunk += event.results[i][0].transcript;
      }
      const merged = chunk.trim();
      textRef.current = merged;
      setLiveText(merged);
    };

    recognition.onerror = (event) => {
      if (event.error !== "aborted") {
        showToast("语音识别失败，请重试");
      }
      setListening(false);
      setLiveText("");
    };

    recognition.onend = () => {
      setListening(false);
      setLiveText("");
      recognitionRef.current = null;
      if (shouldSaveRef.current) {
        window.setTimeout(() => {
          saveReminder(textRef.current);
        }, 120);
      }
      shouldSaveRef.current = false;
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch {
      showToast("无法启动麦克风，请检查浏览器权限");
    }
  }, [saveReminder, showToast]);

  useEffect(() => {
    refreshList();
    const onUpdate = () => refreshList();
    window.addEventListener("voiceReminderUpdated", onUpdate);
    return () => {
      window.removeEventListener("voiceReminderUpdated", onUpdate);
      clearTimeout(toastTimerRef.current);
      recognitionRef.current?.stop?.();
    };
  }, [refreshList]);

  const handlePointerDown = (e) => {
    e.preventDefault();
    if (recognitionRef.current) return;
    startListen();
  };

  const handlePointerUp = () => {
    if (recognitionRef.current || listening) {
      stopListen(true);
    }
  };

  const parsedTime = parseReminderTime(liveText);
  const previewContent = extractReminderContent(liveText) || liveText.trim();

  return (
    <>
      {listening && (
        <div className="fixed bottom-24 right-6 z-[9999] w-72 bg-white rounded-2xl shadow-2xl border border-blue-100 overflow-hidden pointer-events-none">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-4 py-3 text-white flex items-center gap-2">
            <span className="text-lg animate-pulse">🎙️</span>
            <span className="font-medium text-sm">正在备忘…</span>
          </div>
          <div className="px-4 py-3">
            <p className="text-sm text-gray-800 min-h-[2.5rem] leading-relaxed">
              {liveText || "请说话，例如：一分钟后提醒我喝水"}
            </p>
            {parsedTime && (
              <p className="text-xs text-blue-600 bg-blue-50 rounded-lg px-3 py-2 mt-2">
                将提醒：{formatReminderTime(parsedTime)}
                {previewContent ? ` · ${previewContent}` : ""}
              </p>
            )}
            <p className="text-xs text-gray-400 mt-2 text-center">松开手指完成</p>
          </div>
        </div>
      )}

      {!listening && toast && (
        <div className="fixed bottom-24 right-6 z-[9999] max-w-[240px] px-4 py-2.5 rounded-xl text-sm shadow-lg bg-gray-900/90 text-white pointer-events-none">
          {toast}
        </div>
      )}

      <button
        type="button"
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onPointerCancel={handlePointerUp}
        className={`fixed bottom-6 right-6 z-[9999] flex flex-col items-center justify-center w-16 h-16 rounded-full text-white shadow-lg transition select-none touch-none ${
          listening
            ? "bg-red-500 ring-4 ring-red-200 animate-pulse scale-105"
            : "bg-blue-500 hover:bg-blue-600"
        }`}
        title="按住说话，松手设置提醒"
        aria-label="备忘"
      >
        <span className="text-xl leading-none">{listening ? "🎙️" : "📝"}</span>
        <span className="text-xs mt-0.5 font-medium">备忘</span>
      </button>
    </>
  );
}
